/**
 * Theme color resolution for packages, prepaid, rewards, and order request.
 * Supports legacy DB tokens (`theme-1`…`theme-10`) and direct CSS backgrounds.
 */

/** Exact gradients formerly defined as `.theme-1`…`.theme-10` in styles.scss. */
export const LEGACY_THEME_BACKGROUNDS: Readonly<Record<string, string>> = {
  'theme-1': 'linear-gradient(to bottom right, #21BDB8, #280684)',
  'theme-2': 'linear-gradient(to bottom right, #FEDC2A, #DD5789, #7A2C9E)',
  'theme-3': 'linear-gradient(to bottom right, #172368, #E249AF)',
  'theme-4': 'linear-gradient(to bottom right, #9358F7, #10D7E2)',
  'theme-5': 'linear-gradient(to bottom right, #3A42E1, #620C90)',
  'theme-6': 'linear-gradient(to bottom right, #73CCD8, #2B6B9F)',
  'theme-7': 'linear-gradient(to bottom right, #B0325E, #FAA681)',
  'theme-8': 'linear-gradient(to bottom right, #0F5F4B, #09152F)',
  'theme-9': 'linear-gradient(to bottom right, #A1FF8B, #3F93FF)',
  'theme-10': 'linear-gradient(to bottom right, #BD344B, #082440)',
};

export interface ThemeColorPreset {
  id: string;
  bg: string;
  title: string;
}

/**
 * Default palette swatches for the shared theme color picker.
 * Rechecked from site menu board (colorPicker.jpeg), top → bottom:
 * 1 CHAMPION (yellow/cream), 2 GLADIATOR (blue→cyan), 3 WARRIOR (red),
 * 4 CRIME FIGHTER (pale lavender), 5 DEFENDER (purple), 6 HAND WASH (orange-red).
 */
export const THEME_COLOR_PRESETS: readonly ThemeColorPreset[] = [
  { id: 'p1', bg: 'linear-gradient(180deg,#F5E6A8,#E8C45A)', title: 'CHAMPION' },
  { id: 'p2', bg: 'linear-gradient(180deg,#0060D8,#30A8D8)', title: 'GLADIATOR' },
  { id: 'p3', bg: '#D12027', title: 'WARRIOR' },
  { id: 'p4', bg: '#E6E2F0', title: 'CRIME FIGHTER' },
  { id: 'p5', bg: 'linear-gradient(180deg,#9B6AA8,#6D4B88)', title: 'DEFENDER' },
  { id: 'p6', bg: 'linear-gradient(180deg,#E85A20,#B40C00)', title: 'HAND WASH' },
] as const;

export const DEFAULT_THEME_BACKGROUND = THEME_COLOR_PRESETS[0].bg;

export const THEME_CUSTOM_COLORS_STORAGE_KEY = 'way_custom_theme_colors_v1';

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const GRADIENT_DIR_RE =
  /^(to\s+(bottom\s+right|top\s+left|bottom\s+left|top\s+right|right|left|top|bottom)|[0-9.]+deg)$/i;

/**
 * Sanitize a CSS background value for safe style binding.
 * Accepts #hex or linear-gradient with hex stops only.
 */
export function sanitizeThemeBackground(value: string): string | null {
  const v = value.trim().replace(/\s+/g, ' ');
  if (HEX_RE.test(v)) {
    return v.length === 4
      ? `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toUpperCase()
      : v.toUpperCase();
  }
  const m = /^linear-gradient\((.+)\)$/i.exec(v);
  if (!m) {
    return null;
  }
  const inner = m[1];
  if (/url\s*\(|expression\s*\(|javascript:|var\s*\(/i.test(inner)) {
    return null;
  }
  const parts = inner.split(',').map((p) => p.trim());
  if (parts.length < 2) {
    return null;
  }
  const isDirection = GRADIENT_DIR_RE.test(parts[0]);
  const stops = isDirection ? parts.slice(1) : parts;
  if (stops.length < 2 || !stops.every((s) => HEX_RE.test(s))) {
    return null;
  }
  return v;
}

/**
 * Resolve a stored theme_color (legacy token or CSS) to a safe CSS background.
 * Returns null when unset or unsupported.
 */
export function resolveThemeBackground(
  themeColor: string | null | undefined
): string | null {
  const token = String(themeColor ?? '').trim();
  if (!token) {
    return null;
  }
  const legacy = Object.hasOwn(LEGACY_THEME_BACKGROUNDS, token)
    ? LEGACY_THEME_BACKGROUNDS[token]
    : undefined;
  if (typeof legacy === 'string') {
    return legacy;
  }
  return sanitizeThemeBackground(token);
}

/** Min white-on-bg contrast for large text (WCAG AA). Below this → treat as light. */
const MIN_WHITE_TEXT_CONTRAST = 3;

/**
 * Hex stops only — matches what sanitizeThemeBackground / the color picker persist
 * (#hex or linear-gradient with hex stops, plus legacy theme-* tokens).
 */
const THEME_HEX_STOP_RE = /#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/g;

function hexRelativeLuminance(hex: string): number {
  let h = hex.slice(1);
  if (h.length === 3) {
    h = `${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  const int = parseInt(h, 16);
  const channel = (shift: number) => {
    const c = ((int >> shift) & 255) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
}

/**
 * True when a theme background is light enough that white text needs a contrast scrim.
 * Uses the brightest hex stop (worst case for #fff). Reuses resolveThemeBackground().
 */
export function isLightThemeBackground(themeColor: string | null | undefined): boolean {
  const resolved = resolveThemeBackground(themeColor);
  if (!resolved) {
    return false;
  }
  const stops = resolved.match(THEME_HEX_STOP_RE);
  if (!stops?.length) {
    return false;
  }
  const brightest = Math.max(...stops.map(hexRelativeLuminance));
  // Contrast of white (#fff, L=1) vs background: (1.05) / (L + 0.05)
  return 1.05 / (brightest + 0.05) < MIN_WHITE_TEXT_CONTRAST;
}

/** Canonical key for comparing two theme backgrounds (spacing/case insensitive). */
export function themeBackgroundKey(value: string | null | undefined): string | null {
  const resolved = resolveThemeBackground(value);
  if (!resolved) {
    return null;
  }
  return resolved.replace(/\s+/g, '').toLowerCase();
}

export function themeBackgroundsEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const ka = themeBackgroundKey(a);
  const kb = themeBackgroundKey(b);
  return !!ka && !!kb && ka === kb;
}

/** Stable custom-swatch id so the same package color is not duplicated in localStorage. */
export function stableCustomThemeId(bg: string): string {
  const key = themeBackgroundKey(bg) || bg;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash |= 0;
  }
  return 'c_saved_' + (hash >>> 0).toString(36);
}

export interface ThemeCustomSwatch {
  id: string;
  bg: string;
  title: string;
}

export function loadCustomThemeColors(): ThemeCustomSwatch[] {
  try {
    const raw = localStorage.getItem(THEME_CUSTOM_COLORS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (item): item is ThemeCustomSwatch =>
        !!item &&
        typeof item.id === 'string' &&
        typeof item.bg === 'string' &&
        !!sanitizeThemeBackground(item.bg)
    );
  } catch {
    return [];
  }
}

export function saveCustomThemeColors(colors: ThemeCustomSwatch[]): void {
  try {
    localStorage.setItem(THEME_CUSTOM_COLORS_STORAGE_KEY, JSON.stringify(colors));
  } catch {
    // ignore quota / private mode
  }
}

/** Clear browser-local custom palette (call on logout). */
export function clearCustomThemeColors(): void {
  try {
    localStorage.removeItem(THEME_CUSTOM_COLORS_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function hsvToHex(h: number, s: number, v: number): string {
  h = ((h % 360) + 360) % 360;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const to = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return ('#' + to(r) + to(g) + to(b)).toUpperCase();
}

export function hexToHsv(hex: string): { h: number; s: number; v: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) {
    return null;
  }
  const int = parseInt(m[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r) {
      h = ((g - b) / d) % 6;
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) {
      h += 360;
    }
  }
  return { h, s: max ? d / max : 0, v: max };
}
